import {forwardRef, Fragment, useMemo} from "react";
import {useTranslation} from "react-i18next";

// --- Tiptap UI Primitive ---
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "components/RichEditor/components/tiptap-ui-primitive/tooltip";

// --- Lib ---
import {cn, parseShortcutKeys} from "components/RichEditor/lib/tiptap-utils";

import "components/RichEditor/components/tiptap-ui-primitive/button/button-colors.scss";
import "components/RichEditor/components/tiptap-ui-primitive/button/button-group.scss";
import "components/RichEditor/components/tiptap-ui-primitive/button/button.scss";

export const ShortcutDisplay = ({shortcuts}) => {
  if (shortcuts.length === 0) return null;

  return (
    <div>
      {shortcuts.map((key, index) => (
        <Fragment key={index}>
          {index > 0 && <kbd>+</kbd>}
          <kbd>{key}</kbd>
        </Fragment>
      ))}
    </div>
  );
};

export const Button = forwardRef(
  (
    {
      className,
      children,
      tooltip,
      showTooltip = true,
      shortcutKeys,
      variant,
      size,
      ...props
    },
    ref
  ) => {
    const shortcuts = useMemo(() => parseShortcutKeys({shortcutKeys}), [shortcutKeys]);
    const {t} = useTranslation();

    if (!tooltip || !showTooltip) {
      return (
        <button
          className={cn("tiptap-button", className)}
          ref={ref}
          data-style={variant}
          data-size={size}
          {...props}
        >
          {children}
        </button>
      );
    }

    return (
      <Tooltip delay={200}>
        <TooltipTrigger
          className={cn("tiptap-button", className)}
          ref={ref}
          data-style={variant}
          data-size={size}
          {...props}
        >
          {children}
        </TooltipTrigger>
        <TooltipContent>
          {t(tooltip)}
          <ShortcutDisplay shortcuts={shortcuts} />
        </TooltipContent>
      </Tooltip>
    );
  }
);

Button.displayName = "Button";

export const ButtonGroup = forwardRef(
  ({className, children, orientation = "vertical", ...props}, ref) => {
    return (
      <div
        ref={ref}
        className={cn("tiptap-button-group", className)}
        data-orientation={orientation}
        role="group"
        {...props}
      >
        {children}
      </div>
    );
  }
);
ButtonGroup.displayName = "ButtonGroup";

export default Button;
